// 메뉴 아이템 목록 컴포넌트
import { ChevronDown } from 'lucide-react';
import type { OrderData } from './types';
import { receiptTranslations, getSampleCustomizations } from './constants';
import { useApp } from '@/contexts/AppContext';
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency';
import { narrowReceiptView } from '@/utils/receipt/narrowReceiptView';

interface MenuItemsListProps {
  orderData: OrderData;
  expandedItems: number[];
  onToggleItem: (index: number) => void;
}

export function MenuItemsList({
  orderData,
  expandedItems,
  onToggleItem,
}: MenuItemsListProps) {
  // #522: 15 locale 전수 lookup — Locale 단일 출처 정합.
  const { locale, currency } = useApp();
  const t = receiptTranslations[locale];

  const formatCurrency = (amount: number) => formatCurrencyUtil(amount, currency);

  return (
    <>
      {/* 메뉴 헤더 */}
      <div className="grid grid-cols-[2fr_0.8fr_1.2fr] gap-3 px-5 py-3 bg-muted">
        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
          {t.menu}
        </span>
        <span className="text-[10px] font-bold text-muted-foreground text-center uppercase tracking-wider">
          {t.qty}
        </span>
        <span className="text-[10px] font-bold text-muted-foreground text-right uppercase tracking-wider">
          {t.price}
        </span>
      </div>

      {/* 메뉴 아이템 — #336 C: items 폴백을 narrowReceiptView 1곳에 집중 */}
      <div className="border-b-2 border-border">
        {narrowReceiptView(orderData).items.map((item, index) => {
          const customizations =
            item.customizations || getSampleCustomizations(locale, index);
          const isExpanded = expandedItems.includes(index);
          const hasCustomizations = customizations && customizations.length > 0;

          return (
            <div
              key={index}
              className="border-b border-border last:border-b-0"
            >
              {/* 클릭 가능한 헤더 */}
              <button
                onClick={() => hasCustomizations && onToggleItem(index)}
                className={`w-full grid grid-cols-[2fr_0.8fr_1.2fr] gap-3 items-center px-5 py-3.5 transition-colors ${
                  hasCustomizations
                    ? 'hover:bg-muted/70 cursor-pointer active:bg-muted'
                    : 'cursor-default'
                }`}
              >
                <div className="min-w-0 flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground text-left truncate">
                    {item.name}
                  </span>
                  {hasCustomizations && (
                    <ChevronDown
                      className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </div>
                <span className="text-sm font-medium text-foreground text-center">
                  {item.quantity}
                </span>
                <span className="text-sm font-bold text-foreground text-right">
                  {formatCurrency(item.price * item.quantity)}
                </span>
              </button>

              {/* 확장 가능한 커스터마이제이션 */}
              {hasCustomizations && (
                <div
                  className={`transition-all duration-300 ease-in-out bg-muted/30 ${
                    isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <div className="px-5 py-3">
                    <div className="pl-3 border-l-2 border-primary space-y-1.5">
                      {customizations.map((option, optionIndex) => (
                        <p
                          key={optionIndex}
                          className="text-xs text-muted-foreground"
                        >
                          {option}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
